import { z } from "zod";

/**
 * Pantry v2 (Track B, B1, issue #373). Mirrors the `PantryLocation` enum in
 * packages/database/prisma/schema.prisma -- validators can't import from
 * database (see package.json; validators only depends on zod), so this list
 * is kept in sync by hand, same pattern as DIETARY_RESTRICTION_TAGS above.
 */
export const PANTRY_LOCATIONS = ["fridge", "freezer", "pantry", "spice"] as const;
export type PantryLocationInput = (typeof PANTRY_LOCATIONS)[number];

const pantryLocationSchema = z.enum(PANTRY_LOCATIONS);

/**
 * Body schema for POST /pantry (single add) and each entry of POST
 * /pantry/bulk's `items` array. `.strict()` per the security-boundary
 * pattern used across this package. `location`/`openedAt` are both
 * optional -- an omitted `location` falls back to the ingredient's category
 * default (apps/api/src/services/pantry-freshness.ts,
 * defaultLocationForCategory), and an omitted `openedAt` means "not opened".
 */
export const addPantryItemSchema = z
  .object({
    // Not `.uuid()` -- ingredientId values in tests/fixtures across this
    // codebase are plain readable strings ("ing-1"), not real UUIDs; the
    // route's own `prisma.ingredient.findUnique` 404s on any ID that
    // doesn't resolve, which is the real existence check.
    ingredientId: z.string().min(1),
    quantity: z.number().positive().optional(),
    unit: z.string().trim().min(1).max(50).optional(),
    purchasedAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().trim().max(500).optional(),
    location: pantryLocationSchema.optional(),
    openedAt: z.string().datetime({ offset: true }).optional(),
  })
  .strict();

export type AddPantryItemInput = z.infer<typeof addPantryItemSchema>;

/**
 * Body schema for POST /pantry/bulk. Capped at 100 items, matching the
 * route's existing pre-Zod bound (apps/api/src/routes/pantry.ts).
 */
export const bulkAddPantrySchema = z
  .object({
    items: z.array(addPantryItemSchema).min(1).max(100),
  })
  .strict();

export type BulkAddPantryInput = z.infer<typeof bulkAddPantrySchema>;

/**
 * Body schema for POST /pantry/from-staple-guess (#388). The client sends
 * only ingredient ids — every other field (quantity, unit, expiry) is derived
 * server-side from the curated catalogue, because the whole point is that the
 * user supplied no numbers. Capped at 100 to match bulkAddPantrySchema.
 */
export const acceptStapleGuessSchema = z
  .object({
    ingredientIds: z.array(z.string().min(1)).min(1).max(100),
  })
  .strict();

export type AcceptStapleGuessInput = z.infer<typeof acceptStapleGuessSchema>;

/**
 * Body schema for PUT /pantry/:id. Every field is optional (partial
 * update); `quantity`/`unit`/`notes` accept `null` explicitly to clear a
 * previously-set value (matches UpdatePantryItemRequest in
 * apps/web/lib/api/types.ts). `openedAt` also accepts `null` -- unlike
 * "opened", there's no client-facing way to un-set a location, so it stays
 * non-nullable (a pantry item is always somewhere).
 */
export const updatePantryItemSchema = z
  .object({
    quantity: z.number().positive().nullable().optional(),
    unit: z.string().trim().min(1).max(50).nullable().optional(),
    purchasedAt: z.string().datetime({ offset: true }).optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    location: pantryLocationSchema.optional(),
    openedAt: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .strict();

export type UpdatePantryItemInput = z.infer<typeof updatePantryItemSchema>;

/**
 * Pantry v2 (Track B, B5, issue #377). Mirrors the `PantryItemEventOutcome`
 * enum in packages/database/prisma/schema.prisma -- kept in sync by hand for
 * the same reason PANTRY_LOCATIONS above is (validators can't import from
 * database). Deliberately a closed, three-value set: see
 * DEFAULT_NO_EVIDENCE_OUTCOME in apps/api/src/services/pantry-outcomes.ts
 * for why an unspecified removal still resolves to one of these three, never
 * a fourth "unknown" value.
 */
export const PANTRY_EVENT_OUTCOMES = ["consumed", "expired", "discarded"] as const;
export type PantryEventOutcomeInput = (typeof PANTRY_EVENT_OUTCOMES)[number];

/**
 * Body schema for DELETE /pantry/:id. `outcome` is OPTIONAL -- the route
 * auto-detects `consumed` when the household's active meal plan already
 * uses the ingredient (reusing B2's getActivePlanIngredientRecipeMap), and
 * otherwise falls back to the documented no-outcome-DELETE default. Sending
 * an explicit outcome (the web app's 3-option prompt, shown when there's no
 * plan evidence) always wins over auto-detection.
 */
export const removePantryItemSchema = z
  .object({
    outcome: z.enum(PANTRY_EVENT_OUTCOMES).optional(),
  })
  .strict();

export type RemovePantryItemInput = z.infer<typeof removePantryItemSchema>;
